/** Manages the full-screen HTML overlay that sits on top of the kaplay canvas. */

let container: HTMLDivElement | null = null;

const BASE_STYLES: Partial<CSSStyleDeclaration> = {
  position: "fixed",
  inset: "0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Courier New', Courier, monospace",
  background: "#0f0f1a",
  zIndex: "100",
  overflowY: "auto",
};

export function initApp(): void {
  container = document.createElement("div");
  container.id = "bb-app";
  Object.assign(container.style, BASE_STYLES);
  document.body.appendChild(container);
}

export function setScreen(el: HTMLElement): void {
  if (!container) initApp();
  container!.innerHTML = "";
  container!.appendChild(el);
  container!.style.display = "flex";
}

export function hideApp(): void {
  if (container) container.style.display = "none";
}

// ─── Shared style helpers ───────────────────────────────────────────────────

export const colors = {
  bg: "#0f0f1a",
  card: "#16162a",
  border: "#2a2a4a",
  accent: "#ff6b35",
  gold: "#ffd700",
  text: "#e2e2e2",
  muted: "#888",
  danger: "#e05050",
  success: "#50c878",
};

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  styles: Partial<CSSStyleDeclaration> = {},
  attrs: Record<string, string> = {},
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  Object.assign(e.style, styles);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

export function card(widthPx = 480): HTMLDivElement {
  return el("div", {
    background: colors.card,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "36px 40px",
    width: `${widthPx}px`,
    maxWidth: "95vw",
    boxSizing: "border-box",
  });
}

export function title(text: string): HTMLHeadingElement {
  const h = el("h1", {
    margin: "0 0 6px 0",
    fontSize: "28px",
    color: colors.gold,
    letterSpacing: "3px",
    textTransform: "uppercase",
  });
  h.textContent = text;
  return h;
}

export function subtitle(text: string): HTMLParagraphElement {
  const p = el("p", { margin: "0 0 28px 0", color: colors.muted, fontSize: "13px" });
  p.textContent = text;
  return p;
}

export function label(text: string): HTMLLabelElement {
  const l = el("label", {
    display: "block",
    fontSize: "11px",
    color: colors.muted,
    letterSpacing: "1px",
    textTransform: "uppercase",
    marginBottom: "6px",
  });
  l.textContent = text;
  return l;
}

export function input(placeholder = "", type = "text"): HTMLInputElement {
  const i = el("input", {
    width: "100%",
    padding: "10px 12px",
    background: "#0a0a14",
    border: `1px solid ${colors.border}`,
    borderRadius: "4px",
    color: colors.text,
    fontSize: "14px",
    fontFamily: "inherit",
    boxSizing: "border-box",
    outline: "none",
  }, { type, placeholder });
  i.addEventListener("focus", () => { i.style.borderColor = colors.accent; });
  i.addEventListener("blur", () => { i.style.borderColor = colors.border; });
  return i;
}

export function select(options: { value: string; label: string }[]): HTMLSelectElement {
  const s = el("select", {
    padding: "10px 12px",
    background: "#0a0a14",
    border: `1px solid ${colors.border}`,
    borderRadius: "4px",
    color: colors.text,
    fontSize: "14px",
    fontFamily: "inherit",
    outline: "none",
    cursor: "pointer",
  });
  for (const opt of options) {
    const o = document.createElement("option");
    o.value = opt.value;
    o.textContent = opt.label;
    s.appendChild(o);
  }
  s.addEventListener("focus", () => { s.style.borderColor = colors.accent; });
  s.addEventListener("blur", () => { s.style.borderColor = colors.border; });
  return s;
}

export function btn(
  text: string,
  variant: "primary" | "secondary" | "ghost" = "primary",
): HTMLButtonElement {
  const styles: Partial<CSSStyleDeclaration> = {
    padding: "11px 24px",
    borderRadius: "4px",
    border: "none",
    fontSize: "14px",
    fontFamily: "inherit",
    fontWeight: "bold",
    letterSpacing: "1px",
    cursor: "pointer",
    transition: "opacity 0.15s, transform 0.1s",
  };
  if (variant === "primary") {
    Object.assign(styles, { background: colors.accent, color: "#fff" });
  } else if (variant === "secondary") {
    Object.assign(styles, { background: "transparent", color: colors.text, border: `1px solid ${colors.border}` });
  } else {
    Object.assign(styles, { background: "transparent", color: colors.muted, border: "none", padding: "8px 0" });
  }
  const b = el("button", styles);
  b.textContent = text;
  b.addEventListener("mouseenter", () => { b.style.opacity = "0.85"; });
  b.addEventListener("mouseleave", () => { b.style.opacity = "1"; });
  b.addEventListener("mousedown", () => { b.style.transform = "scale(0.97)"; });
  b.addEventListener("mouseup", () => { b.style.transform = "scale(1)"; });
  return b;
}

export function errorMsg(): HTMLParagraphElement {
  const p = el("p", {
    color: colors.danger,
    fontSize: "13px",
    margin: "8px 0 0 0",
    minHeight: "18px",
  });
  return p;
}

export function divider(): HTMLHRElement {
  return el("hr", {
    border: "none",
    borderTop: `1px solid ${colors.border}`,
    margin: "24px 0",
  });
}

export function row(...children: HTMLElement[]): HTMLDivElement {
  const d = el("div", { display: "flex", gap: "10px", alignItems: "center" });
  for (const c of children) d.appendChild(c);
  return d;
}

export function group(labelText: string, input: HTMLElement): HTMLDivElement {
  const d = el("div", { marginBottom: "16px" });
  d.appendChild(label(labelText));
  d.appendChild(input);
  return d;
}
